/* eslint-disable no-underscore-dangle */
import { Coordinates } from "@hermes/api-model-common";
import { Locale } from "@hermes/locale";
import {
    PIN_DARK_MARKER_CLUSTERER,
    PIN_WHITE_MARKER_CLUSTERER,
} from "@hermes/map-utils";
import { calculateDistance } from "@hermes/utils-generic/helpers";

import { MarkerData, MarkerIcon } from "../../map.type";
import {
    BMapTextIconOverlay,
    BMapTextIconOverlayEventName,
} from "../marker-clusterer";

/**
 * Generate a new BMap.Point from Coordinates object
 */
export const getBMapPoint = (coordinates: Coordinates) =>
    new BMap.Point(coordinates.longitude, coordinates.latitude);

/**
 * Generate a new BMap.Marker from a MarkerData object
 */
export const getBMapMarker = (marker: MarkerData): BMap.Marker =>
    new BMap.Marker(getBMapPoint(marker.coordinates), {
        icon: getBMapIcon(marker.icon),
    });

/**
 * Generate a new BMap.Icon from a MarkerIcon object
 */
export const getBMapIcon = (markerIcon: MarkerIcon): BMap.Icon => {
    const size = getBMapSize({
        width: markerIcon.size.width,
        height: markerIcon.size.height,
    });
    const icon = new BMap.Icon(markerIcon.url, size);
    icon.setImageSize(size);
    return icon;
};

/**
 * Generate a new BMap.Size from width and height numbers
 */
export const getBMapSize = ({
    width,
    height,
}: {
    width: number;
    height: number;
}): BMap.Size => new BMap.Size(width, height);

/**
 * Describes if an overlay is of type Marker
 */
export const isBMapMarker = (overlay: BMap.Overlay): overlay is BMap.Marker =>
    overlay instanceof BMap.Marker;

/**
 * Returns all Markers from the Overlay list
 */
export const getMarkers = (overlays: BMap.Overlay[]): BMap.Marker[] =>
    overlays.filter(isBMapMarker);

/**
 * Describes if an overlay is of type TextIconOverlay
 */
export const isBMapTextIconOverlay = (
    overlay: BMap.Overlay,
    window: Window,
): overlay is BMapTextIconOverlay =>
    overlay instanceof window.BMapLib.TextIconOverlay;

/**
 * Returns all BMapTextIconOverlay from the Overlay list
 */
export const getBMapTextIconOverlays = (
    overlays: BMap.Overlay[],
    window: Window,
): BMapTextIconOverlay[] =>
    overlays.filter((overlay): overlay is BMapTextIconOverlay =>
        isBMapTextIconOverlay(overlay, window),
    );

/**
 * Returns the closest point of a point called "center"
 *
 * @param markers All markers to check
 * @param center Point to center
 * @returns The closest point to the center
 */
export const getClosestMarker = (
    markers: MarkerData[],
    center: Coordinates,
): MarkerData =>
    [...markers].sort(
        (markerA, markerB) =>
            calculateDistance(markerA.coordinates, center) -
            calculateDistance(markerB.coordinates, center),
    )[0];

/**
 * Extends bounds to fit a point
 *
 * @param coordinates to fit with
 * @param bounds that will be extended
 */
export const extendBounds = (
    coordinates: Coordinates,
    bounds: BMap.Bounds,
): void => {
    const point = getBMapPoint(coordinates);
    bounds.extend(point);
};

export const addHoverListeners = (
    overlays: BMapTextIconOverlay[],
    locale: Locale,
): void => {
    const events: BMapTextIconOverlayEventName[] = ["mouseover", "mouseout"];
    overlays.forEach((overlay) =>
        events.forEach((eventName) => {
            overlay.addEventListener(eventName, ({ target }) => {
                const overlayStyles = target._domElement.style;
                if (eventName === "mouseover") {
                    overlayStyles.backgroundImage = `url(${locale.urlPrefix}${PIN_WHITE_MARKER_CLUSTERER}`;
                    overlayStyles.color = "#000000";
                    return;
                }
                overlayStyles.backgroundImage = `url(${locale.urlPrefix}${PIN_DARK_MARKER_CLUSTERER}`;
                overlayStyles.color = "#ffffff";
            });
        }),
    );
};
